import { format, addDays } from "date-fns";

/**
 * Computes the weekly forecast: how many cards are due for review per day
 * over the next 7 days (Today, Tomorrow, Today+2, ... Today+6).
 *
 * - Overdue cards (next_review < today) count for "Today".
 * - Cards with next_review in the 7-day window count for that specific day.
 * - Cards with next_review beyond the window are not included.
 * - Uses local timezone for date grouping (format avoids UTC shift).
 *
 * @param cards - Array of cards with optional next_review (ISO string)
 * @returns Array of { date: YYYY-MM-DD, count } for the 7 days
 */
export function computeWeeklyForecast(
  cards: { next_review?: string | null }[]
): { date: string; count: number }[] {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const todayStr = format(now, "yyyy-MM-dd");

  const dayLabels: string[] = [];
  const dayCounts: Record<string, number> = {};
  for (let i = 0; i < 7; i++) {
    const d = addDays(now, i);
    const key = format(d, "yyyy-MM-dd");
    dayLabels.push(key);
    dayCounts[key] = 0;
  }

  for (const c of cards) {
    const next = c.next_review;
    if (!next) continue;
    const key = next.slice(0, 10);
    if (key < todayStr) {
      dayCounts[todayStr]++;
    } else if (key in dayCounts) {
      dayCounts[key]++;
    }
  }

  return dayLabels.map((date) => ({
    date,
    count: dayCounts[date],
  }));
}
